import { useCallback, useMemo } from 'react';
import { Button } from '@library/components/buttons';
import { DEFAULT_ADMIN_USER } from '@bo-constants/member/memberNotes';
import type { FormattedMemberNote } from '@library/types/members/memberNotes';
import GrayTrashIcon from '@library/assets/images/icons/gray-trash-icon.svg';

export interface MemberNotesTableRowProps {
  note: FormattedMemberNote;
  onDeleteNote: (id: string) => void;
}

export const MemberNotesTableRow = ({
  note,
  onDeleteNote
}: MemberNotesTableRowProps) => {
  const { adminUser, body, createdDateTime, id } = note;
  const { formattedDate: createdDate, formattedTime: createdTime } =
    createdDateTime;
  const { email: userEmail, name: userName } = adminUser ?? DEFAULT_ADMIN_USER;

  const handleDeleteNote = useCallback(() => {
    onDeleteNote(id);
  }, [id, onDeleteNote]);

  const adminUserDetails = useMemo(() => {
    if (!userName && !userEmail) {
      return (
        <p className="text-soClean-170 font-heebo block text-xs font-medium tracking-tight">
          Admin user not available
        </p>
      );
    }

    return (
      <>
        {userName && (
          <p className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight">
            {userName}
          </p>
        )}
        {userEmail && (
          <p className="text-soClean-170 font-heebo block text-xs font-medium tracking-tight">
            {userEmail}
          </p>
        )}
      </>
    );
  }, [userName, userEmail]);

  return (
    <div
      className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 text-2xs relative flex h-full min-h-16 cursor-default items-center border-b xl:text-xs"
      data-testid={`member-note-row-${id}`}
    >
      <div
        className="flex w-[30%] flex-col p-3"
        data-testid="member-note-admin-user"
      >
        {adminUserDetails}
      </div>
      <p
        className="flex w-1/5 flex-col p-3"
        data-testid="member-note-createdAt"
      >
        <span className="leading-5">{createdDate}</span>
        <span className="text-soClean-160">{createdTime}</span>
      </p>
      <p
        className="flex flex-1 whitespace-pre-line break-all p-3 leading-4"
        data-testid="member-note-description"
      >
        {body}
      </p>
      <Button className="p-3" onClick={handleDeleteNote}>
        <img src={GrayTrashIcon} className="size-5" alt="Delete Note" />
      </Button>
    </div>
  );
};
